import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Package, Mail, Lock, User, Building2, Phone, AlertCircle, Clock, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { register, error } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    customer_id: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/companies`).then(res => {
      setCompanies(res.data.companies);
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }
    if (!formData.customer_id) {
      setLocalError('Please select your company');
      return;
    }

    setLoading(true);
    try {
      const selectedCompany = companies.find(c => c.customer_id === formData.customer_id);
      const result = await register({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        customer_id: formData.customer_id,
        company_name: selectedCompany?.company_name || '',
        password: formData.password,
      });
      if (result.pending_approval) {
        setPendingApproval(true);
      }
    } catch (err) {
      setLocalError(err.response?.data?.detail || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError || error;

  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <img src="/cargodash-logo.svg" alt="CargoDash" className="h-10" />
            </Link>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center" data-testid="pending-approval-screen">
            <div className="h-16 w-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Account Pending Approval</h2>
            <p className="text-slate-600 mb-6">
              Your registration has been submitted successfully. An administrator will review and activate your account shortly.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-800 mb-1">What happens next?</p>
                  <ul className="space-y-1 list-disc list-inside text-slate-500">
                    <li>Your company admin has been notified</li>
                    <li>Once approved, you can log in with your credentials</li>
                    <li>You'll be able to create waybills and track shipments</li>
                  </ul>
                </div>
              </div>
            </div>
            <Link to="/login">
              <Button className="w-full btn-primary" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <img src="/cargodash-logo.svg" alt="CargoDash" className="h-10" />
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Create your account</h1>
          <p className="text-slate-600 mt-1">Join your company on CargoDash</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {displayError && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-6" data-testid="register-error">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-600">{displayError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="label-text">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="pl-10 input-field"
                  placeholder="John Smith"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="label-text">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 input-field"
                  placeholder="you@company.com"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="label-text">Phone Number</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 input-field"
                  placeholder="+27 11 555 0000"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="customer_id" className="label-text">Company</Label>
              <Select value={formData.customer_id} onValueChange={(val) => setFormData({ ...formData, customer_id: val })}>
                <SelectTrigger className="mt-1" data-testid="register-company-select">
                  <Building2 className="h-4 w-4 text-slate-400 mr-2" />
                  <SelectValue placeholder="Select your company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="password" className="label-text">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-10 pr-10 input-field"
                  placeholder="Min 6 characters"
                  data-testid="register-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="label-text">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="pl-10 pr-10 input-field"
                  placeholder="Confirm your password"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="register-submit-btn">
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-teal-700 hover:text-teal-600" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
